import React, { useContext } from "react";
import { Button, Card, Col, Container, Form, Modal, Row } from "react-bootstrap";
import { ValidatorHelper } from "../Shared/Utility/ValidatorHelper";
import InputMask from "react-input-mask"
import AbpnSpinner from "../Shared/Components/AbpnSpinner";
import { abpnClient } from "../Services/AbpnClient";
import { AuthContext } from "../Auth/AuthProvider";
import { TrainingAttestation } from "../Training/Models/TrainingAttestation";
import { useNavigate } from "react-router";
import { ProfileContext } from "../Auth/ProfileProvider";

const hasNumberRegex = /\d/;

interface ISearchCriteria {
    firstName: string | null;
    lastName: string | null;
    dateOfBirth: Date | null;
    ssn4: string | null;
}

interface IResidentInfo {
    physicianId: number;
    trainingProgramId: number | null;
    firstName: string;
    lastName: string;
    dateOfBirth: Date;
    ssn4: string;
    trainingSpecialty: string;
    lastProgramName: string;
    lastPGY: string;
    attendedTrainingProgramIds: Array<number>;
    trainingAttestations: Array<TrainingAttestation>;
}

export function TransferResidents() {
    const authContext = React.useContext(AuthContext);
    const { user } = useContext(ProfileContext);

    const [searchCriteria] = React.useState<ISearchCriteria>({firstName: null, lastName: null, dateOfBirth: null, ssn4: null});
    const [isSearching, setIsSearching] = React.useState<boolean>(false);
    const [residents, setResidents] = React.useState<Array<IResidentInfo>>(null);
    const [isFormValid, setIsFormValid] = React.useState<boolean>(null);

    const searchFormRef = React.useRef<HTMLFormElement>(null);
    const firstNameRef = React.useRef<HTMLInputElement>(null);
    const lastNameRef = React.useRef<HTMLInputElement>(null);
    const dateOfBirthRef = React.useRef<HTMLInputElement>(null);
    const ssn4Ref = React.useRef<HTMLInputElement>(null);
    const navigate = useNavigate();

    const updateFirstName = () => {
        searchCriteria.firstName = firstNameRef.current.value;

        validateFirstName();
    }

    const validateFirstName = () => {
        const element = firstNameRef.current;

        ValidatorHelper.clearCustomValidation(element);
        if (hasNumberRegex.test(searchCriteria.firstName)) {
            ValidatorHelper.setCustomValidationWithFeedback(element, "Enter letters only.");
        }
    }

    const updateLastName = () => {
        searchCriteria.lastName = lastNameRef.current.value;

        validateLastName();
    }

    const validateLastName = () => {
        const element = lastNameRef.current;

        ValidatorHelper.clearCustomValidation(element);
        if (searchCriteria.lastName === null || searchCriteria.lastName === '') {
            ValidatorHelper.setCustomValidationWithFeedback(element, "Last name is required.");
        }

        if (hasNumberRegex.test(searchCriteria.lastName)) {
            ValidatorHelper.setCustomValidationWithFeedback(element, "Enter letters only.");
        }
    }

    const updateDateOfBirth = () => {
        searchCriteria.dateOfBirth = new Date(dateOfBirthRef.current.value);

        validateDateOfBirth();
    }

    const validateDateOfBirth = () => {
        const element = dateOfBirthRef.current;

        ValidatorHelper.clearCustomValidation(element);
        if (searchCriteria.dateOfBirth === null || isNaN(searchCriteria.dateOfBirth.valueOf())) {
            ValidatorHelper.setCustomValidationWithFeedback(element, "Date of Birth is required.");
        }
    }

    const updateSsn4 = () => {
        searchCriteria.ssn4 = ssn4Ref.current.value.replaceAll('_', '');

        validateSsn4();
    }

    const validateSsn4 = () => {
        const element = ssn4Ref.current;

        ValidatorHelper.clearCustomValidation(element);
        if (searchCriteria.ssn4 !== null && searchCriteria.ssn4 !== '' && searchCriteria.ssn4.length !== 4) {
            ValidatorHelper.setCustomValidationWithFeedback(element, "SSN must be exactly 4 digits.");
        }
    }

    const search = async () => {
        const form = searchFormRef.current;

        // Check Validation On Search
        validateFirstName();
        validateLastName();
        validateDateOfBirth();
        validateSsn4();

        if (!form.checkValidity()) {
            setIsFormValid(false);

            return;
        }


        setIsFormValid(true);
        setIsSearching(true);

        var results = await abpnClient.post<Array<IResidentInfo>>("/api/dashboard/GetTransferResidents", searchCriteria, authContext);
        setResidents(results);

        setIsSearching(false);
    }

    const residentTransered = () => {
        setResidents([]);

        navigate("/");
    }

    return (
        <React.Fragment>
            <Container>
                <Card.Title className="mb-0 fw-bolder">
                    <span className="text-primary">Find a Resident</span>
                </Card.Title>
                <span>Enter one or more search criteria (<span className="text-primary ml-2">*</span> Required)</span>
                <Form ref={searchFormRef} noValidate validated={isFormValid === false}>
                    <Row className="align-items-end mb-3">
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label className="fw-bolder mb-1 pc-font-size-smaller">First Name</Form.Label>
                                <Form.Control ref={firstNameRef} disabled={isSearching} type="input" tabIndex={1} onChange={(_: any) => updateFirstName() }></Form.Control>
                                <Form.Control.Feedback type="invalid"></Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label className="fw-bolder mb-1 pc-font-size-smaller">Last Name<span className="text-primary ml-2">*</span></Form.Label>
                                <Form.Control ref={lastNameRef} disabled={isSearching} required type="input" tabIndex={2} onChange={(_: any) => updateLastName() }></Form.Control>
                                <Form.Control.Feedback type="invalid"></Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label className="fw-bolder mb-1 pc-font-size-smaller">Date of Birth<span className="text-primary ml-2">*</span></Form.Label>
                                <InputMask mask="99/99/9999"
                                    disabled={isSearching}
                                    onChange={(_: any) => updateDateOfBirth() }>
                                    {(inputProps: any) => {
                                        return <Form.Control ref={dateOfBirthRef} {...inputProps} placeholder="MM/DD/YYYY" required type="input" tabIndex={3} />
                                    }}
                                </InputMask>
                                <Form.Control.Feedback type="invalid"></Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label className="fw-bolder mb-1 pc-font-size-smaller">SSN4</Form.Label>
                                <InputMask mask="9999"
                                    disabled={isSearching}
                                    onChange={(_: any) => updateSsn4() }>
                                    {(inputProps: any) => {
                                        return <Form.Control ref={ssn4Ref} {...inputProps} placeholder="1234" type="input" tabIndex={4} />
                                    }}
                                </InputMask>
                                <Form.Control.Feedback type="invalid"></Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col>
                            <Button variant="primary" disabled={isSearching} type="button" onClick={() => search()} tabIndex={5}>Search</Button>
                        </Col>
                    </Row>
                </Form>
            </Container>
            <hr />
            <div>
                <p>To transfer a resident into your program, please select the Transfer In button. When the transfer is complete, you will find the resident added to your active roster.</p>
                <p>You can only transfer in a resident who is currently not in another program's active roster. If the resident you need to transfer in is still active in a program, then that program has not completed entering information or updating the status of the resident. You may need to ask the resident to contact the program coordinator or contact the program coordinator yourself, if possible, to ask them to finish the data entry process for the resident and then remove the resident from their program in the preCERT system.</p>
                <p>If the resident you are searching has been removed from the previous program and does not return in the results, please contact the ABPN office by email at <a href="mailto:credentials@abpn.org">credentials@abpn.org</a> or call <a href="tel:+18472296510">847-229-6510</a>.</p>
                <p className="fw-bolder">You can not return a resident from your program's alumni roster back onto your active roster using the transfer feature. If you would like to return a resident from your alumni roster back onto your active roster, please use the 'Begin Return to Active Roster' button located at the bottom of the resident details page.</p>
            </div>
            <hr />
            <Container>
                { isSearching && <AbpnSpinner /> }
                { !isSearching && residents && residents.length === 0 && <span>No Residents Found</span>}
                { !isSearching && residents && residents.map((resident: IResidentInfo, index: number) =>
                        <React.Fragment>
                            { index !== 0 && <hr /> }
                            <TransferResidentCard key={resident.physicianId} resident={resident} trainingProgramId={user.selectedTrainingProgramId} onResidentTransfered={residentTransered} />
                        </React.Fragment>
                        )
                }
            </Container>
        </React.Fragment>
    );
}

interface ITransferResidentCardProps {
    onResidentTransfered?: (physicianId: number) => void;
    resident: IResidentInfo;
    trainingProgramId: number;
}

function TransferResidentCard(props: ITransferResidentCardProps = { onResidentTransfered: (_: number) => { }, trainingProgramId: 0, resident: null }) {
    const authContext = React.useContext(AuthContext);

    const [showConfirmation, setShowConfirmation] = React.useState<boolean>(false);
    const [isTransfering, setIsTransfering] = React.useState<boolean>(false);

    const alumniOfCurrentProgram = props.resident.trainingProgramId == null && (props.resident.trainingAttestations.find(_ => _.trainingProgramId === props.trainingProgramId && _.isAttestedComplete) || props.resident.attendedTrainingProgramIds.find(_ => _ === props.trainingProgramId)) ? true : false;

    const confirm = async (confirmation: boolean) => {
        const { onResidentTransfered, trainingProgramId } = props;
        const { physicianId } = props.resident;

        setIsTransfering(true);

        if (confirmation) {
            const url = `/api/training/transferResident?physicianId=${physicianId}&trainingProgramId=${trainingProgramId}`;
            await abpnClient.post(url, {}, authContext);
        }

        setIsTransfering(false);
        setShowConfirmation(false);

        if (confirmation) {
            onResidentTransfered(physicianId);
        }
    }

    return (
        <React.Fragment>
            <Modal show={showConfirmation} onHide={()=> confirm(false)}>
                <Modal.Body>Are you sure you want to transfer this resident into your program?</Modal.Body>
                <Modal.Footer>
                    { isTransfering && <AbpnSpinner /> }
                    { !isTransfering &&
                        <React.Fragment>
                            <Button variant="primary" disabled={isTransfering} onClick={() => confirm(true)}>
                                Confirm
                            </Button>
                            <Button variant="outline-secondary" disabled={isTransfering} onClick={() => confirm(false)}>
                                Cancel
                            </Button>
                        </React.Fragment>
                    }
                </Modal.Footer>
            </Modal>
            <Container fluid>
                <Row className="align-items-end mb-3">
                    <Col md={2}>
                        <Card.Title className="fw-bolder">{props.resident.firstName} {props.resident.lastName}</Card.Title>
                        <div>DOB: {new Date(props.resident.dateOfBirth).toLocaleDateString()}</div>
                        <div>SSN4: {props.resident.ssn4}</div>
                        <Button className="mt-2" variant="primary" disabled={props.resident.trainingProgramId !== null || alumniOfCurrentProgram} type="button" onClick={() => setShowConfirmation(true)}>Transfer In</Button>
                    </Col>
                    <Col md={10}>
                        <Row>
                            <Col md={8}>
                                <div className="fw-bold">Current/Last Program</div>
                                <div>{props.resident.lastProgramName}</div>
                            </Col>
                            <Col md={3}>
                                <div className="fw-bold">Status</div>
                                <div>{props.resident.trainingProgramId === null ? "Alumni - Not In Program" : "Active - In Program"}</div>
                            </Col>
                        </Row>
                        <hr />
                        <Row>
                            <Col md={8}>
                                <div className="fw-bold">Training Specialty</div>
                                <div>{props.resident.trainingSpecialty}</div>
                            </Col>
                            <Col md={3}>
                                <div className="fw-bold">Last PGY</div>
                                <div>{props.resident.lastPGY}</div>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </Container>
        </React.Fragment>
    );
};
